import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    ActionGetBookingsDashboard,
    ActionGetRoomDashboardData,
    ActionGetCheckIns,
    ActionGetCheckOuts,
    ActionGetInHouse,
} from '../../../../store/Actions/MasterDataAction';
import { GetAllServiceSlot } from '../../../../store/Actions/ServiceSlotAction';
import { generateQueryString } from '../../../../utils/functions';
import dayjs from 'dayjs';

const useRoomDashboardData = ({ dashboardDate, timelineRange, activeTab, updateUrl, refreshTrigger }) => {
    const dispatch = useDispatch();
    const { roomDashboard, checkIns, checkOuts, inHouse, loader } = useSelector((state) => state.MasterData);
    const [mediDaycareCount, setMediDaycareCount] = useState(0);

    const fetchDashboardData = useCallback((date) => {
        const query = `?date=${date}`;
        dispatch(ActionGetRoomDashboardData({ Query: query }));
        dispatch(ActionGetCheckIns({ Query: query }));
        dispatch(ActionGetCheckOuts({ Query: query }));
        dispatch(ActionGetInHouse({ Query: query }));

        updateUrl({
            date,
            startDate: null,
            endDate: null
        });
    }, [dispatch, updateUrl]);

    const fetchCheckInsData = useCallback(async (params) => {
        const list = checkIns || [];
        const { page, perPage } = params;
        const startIndex = (page - 1) * perPage;
        const endIndex = startIndex + perPage;
        return {
            data: list.slice(startIndex, endIndex),
            total: list.length
        };
    }, [checkIns]);

    const fetchMediDaycareCheckIn = useCallback(async (params) => {
        const { status = 'Upcoming' } = params;
        const queryParams = new URLSearchParams();
        if (status) queryParams.append('status', status);
        if (dashboardDate) queryParams.append('date', dayjs(dashboardDate).format('DD-MM-YYYY'));

        const queryString = `?${queryParams.toString()}`;

        return new Promise((resolve) => {
            dispatch(
                GetAllServiceSlot({
                    Query: queryString,
                    callBack: (responseData) => {
                        setMediDaycareCount(responseData?.totalDocs || 0);
                        resolve({
                            data: responseData?.docs || [],
                            total: responseData?.totalDocs || 0,
                        });
                    },
                })
            );
        });
    }, [dispatch, dashboardDate, refreshTrigger]);

    const fetchInHouseData = useCallback(async (params) => {
        const list = inHouse || [];
        const { page, perPage } = params;
        const startIndex = (page - 1) * perPage;
        const endIndex = startIndex + perPage;
        return {
            data: list.slice(startIndex, endIndex),
            total: list.length
        };
    }, [inHouse]);

    const fetchCheckOutsData = useCallback(async (params) => {
        const list = checkOuts || [];
        const { page, perPage } = params;
        const startIndex = (page - 1) * perPage;
        const endIndex = startIndex + perPage;
        return {
            data: list.slice(startIndex, endIndex),
            total: list.length
        };
    }, [checkOuts]);

    // Dashboard effect
    useEffect(() => {
        if (activeTab !== '1') return;
        fetchDashboardData(dashboardDate);
    }, [activeTab, dashboardDate, fetchDashboardData]);

    // Fetch Medi-Daycare count separately so it shows in the tab header
    useEffect(() => {
        if (activeTab === '1') {
            fetchMediDaycareCheckIn({ status: 'Upcoming' });
        }
    }, [activeTab, dashboardDate, refreshTrigger, fetchMediDaycareCheckIn]);

    // Timeline effect
    useEffect(() => {
        if (activeTab !== '2') return;

        let query = '';
        if (timelineRange.startDate && timelineRange.endDate) {
            query = generateQueryString(
                ['startDate', timelineRange.startDate],
                ['endDate', timelineRange.endDate]
            );
        }

        dispatch(ActionGetBookingsDashboard({ Query: query }));

        updateUrl({
            startDate: timelineRange.startDate,
            endDate: timelineRange.endDate,
            date: null
        });
    }, [activeTab, timelineRange, dispatch, updateUrl]);

    return {
        roomDashboard,
        checkIns,
        checkOuts,
        inHouse,
        mediDaycareCount,
        loader,
        fetchDashboardData,
        fetchCheckInsData,
        fetchCheckOutsData,
        fetchMediDaycareCheckIn,
        fetchInHouseData
    };
};

export default useRoomDashboardData;
